import React, { useRef } from "react";
import CopyImageChip from "./CopyImageChip";
import { formatWeekRange } from "./cupStats";

const scoreForWeek = (scores, week) => scores?.[week] ?? scores?.[String(week)];

const formatScore = (score) =>
    score == null || !Number.isFinite(Number(score)) ? "—" : Number(score).toFixed(2);

const weekHeader = (week, weekLabels) => {
    const label = weekLabels?.[week] || weekLabels?.[String(week)];
    if (!label) return `W${week}`;
    const match = /^week\s*(\d+)$/i.exec(label);
    return match ? `W${match[1]}` : label;
};

const TeamRow = ({
    team,
    seed,
    weeks,
    weekScores,
    cumulativeScore,
    isWinner,
    isWildcard,
}) => (
    <tr className={`h-[50px] ${isWinner ? "bg-green-light" : ""}`}>
        <td className="border-t border-[#efebe0] py-2.5 pl-3 pr-1.5 align-middle">
            <div className="flex min-w-0 items-center gap-2">
                <span
                    className={`grid h-5 w-5 flex-none place-items-center rounded text-[10px] font-extrabold ${
                        isWinner
                            ? "bg-green-mid text-white"
                            : "bg-bg text-text-muted"
                    }`}
                >
                    {seed ?? "–"}
                </span>
                <div className="min-w-0">
                    <div
                        className="flex min-w-0 items-start gap-1.5"
                    >
                        <span
                            className={`min-w-0 break-words text-[12.5px] font-extrabold leading-tight ${
                                isWinner ? "text-forest" : "text-text-mid"
                            }`}
                        >
                            {team ? team.owner_name || team.team_name : "TBD"}
                        </span>
                        {isWildcard && (
                            <span className="mt-px flex-none rounded-sm bg-mustard px-1 py-px text-[8px] font-extrabold tracking-wide text-forest">
                                WC
                            </span>
                        )}
                    </div>
                    {team?.owner_name && team.owner_name !== team.team_name && (
                        <div className="mt-0.5 break-words text-[9.5px] font-semibold leading-tight text-text-muted">
                            {team.team_name}
                        </div>
                    )}
                </div>
            </div>
        </td>
        {weeks.map((week) => (
            <td
                key={week}
                className="border-t border-[#efebe0] px-1.5 py-2.5 text-right text-[11.5px] font-bold tabular-nums text-text-muted"
            >
                {formatScore(scoreForWeek(weekScores, week))}
            </td>
        ))}
        <td
            className={`border-t border-[#efebe0] py-2.5 pl-1.5 pr-3 text-right text-[12.5px] font-black tabular-nums ${
                isWinner ? "text-green" : "text-ds-text"
            }`}
        >
            {formatScore(cumulativeScore)}
        </td>
    </tr>
);

const Match = ({ match, round, wildcardIds, weekLabels }) => {
    const weeks = Array.from(
        { length: round.end_week - round.start_week + 1 },
        (_, index) => round.start_week + index
    );
    const teamColumnWidth = weeks.length === 2 ? "46%" : "52%";
    const scoreColumnWidth = `${(100 - parseFloat(teamColumnWidth)) / (weeks.length + 1)}%`;
    const homeWins =
        match.winner_provider_team_id &&
        match.winner_provider_team_id === match.home?.provider_team_id;
    const awayWins =
        match.winner_provider_team_id &&
        match.winner_provider_team_id === match.away?.provider_team_id;
    return (
        <div className="overflow-hidden rounded-[10px] border border-border bg-white">
            <table
                className="w-full table-fixed border-collapse font-sans"
                aria-label={`${match.home?.owner_name || match.home?.team_name || "TBD"} vs ${
                    match.away?.owner_name || match.away?.team_name || "TBD"
                }`}
            >
                <colgroup>
                    <col style={{ width: teamColumnWidth }} />
                    {weeks.map((week) => (
                        <col key={week} style={{ width: scoreColumnWidth }} />
                    ))}
                    <col style={{ width: scoreColumnWidth }} />
                </colgroup>
                <thead className="bg-bg/70">
                    <tr>
                        <th className="py-2 pl-3 pr-1.5 text-left text-[8.5px] font-extrabold uppercase tracking-[0.14em] text-text-muted">
                            Team
                        </th>
                        {weeks.map((week) => (
                            <th
                                key={week}
                                className="whitespace-nowrap px-1.5 py-2 text-right text-[8.5px] font-extrabold uppercase tracking-wider text-text-muted"
                            >
                                {weekHeader(week, weekLabels)}
                            </th>
                        ))}
                        <th className="py-2 pl-1.5 pr-3 text-right text-[8.5px] font-extrabold uppercase tracking-wider text-text-muted">
                            Total
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <TeamRow
                        team={match.home}
                        seed={match.home_seed}
                        weeks={weeks}
                        weekScores={match.home_week_scores}
                        cumulativeScore={
                            match.home_cumulative_score ?? match.home_score
                        }
                        isWinner={homeWins}
                        isWildcard={wildcardIds.has(match.home?.provider_team_id)}
                    />
                    <TeamRow
                        team={match.away}
                        seed={match.away_seed}
                        weeks={weeks}
                        weekScores={match.away_week_scores}
                        cumulativeScore={
                            match.away_cumulative_score ?? match.away_score
                        }
                        isWinner={awayWins}
                        isWildcard={wildcardIds.has(match.away?.provider_team_id)}
                    />
                </tbody>
            </table>
        </div>
    );
};

/**
 * The knockout bracket panel: one column per round, joined by connector links
 * on wide screens, with the champion shown beneath the final matchup.
 */
const CupBracketPanel = ({ cup }) => {
    const ref = useRef(null);
    const bracket = cup?.bracket || [];
    const champion = cup?.champion;

    const wildcardIds = new Set(
        (cup?.groups || [])
            .flatMap((group) => group.table || [])
            .filter((row) => row.wildcard)
            .map((row) => row.provider_team_id)
    );

    return (
        <div
            ref={ref}
            className="overflow-hidden rounded-2xl border border-border bg-surface shadow-card"
        >
            <div className="flex items-center justify-between border-b-[1.5px] border-[#e7e2d5] px-[18px] py-3.5">
                <h3 className="font-display text-[19px] font-black text-ds-text">
                    Knockout Bracket
                </h3>
                <CopyImageChip targetRef={ref} fileName="league-cup-bracket" />
            </div>

            {bracket.length === 0 ? (
                <p className="px-[18px] py-6 text-sm font-sans font-semibold text-text-muted">
                    {cup?.status === "draft"
                        ? `The bracket forms once the Group Stage wraps up in week ${
                              cup?.schedule?.group_stage_weeks?.slice(-1)[0] ?? "—"
                          }.`
                        : "The knockout bracket will appear here once the Group Stage is complete."}
                </p>
            ) : (
                <div className="flex flex-col items-stretch gap-4 overflow-x-auto p-6 lg:flex-row lg:items-start lg:gap-20">
                    {bracket.map((round, index) => (
                        <React.Fragment key={round.round_index}>
                            <div className="min-w-0 flex-1 lg:min-w-[360px]">
                                <div className="relative">
                                    <p className="mb-3 text-center text-[10px] font-sans font-extrabold uppercase tracking-[0.2em] text-text-muted">
                                        <span className="text-green">{round.label}</span>
                                        {" · "}
                                        {formatWeekRange(
                                            round.start_week,
                                            round.end_week
                                        )}
                                    </p>
                                    <div className="relative grid auto-rows-fr gap-3.5">
                                        {round.matches.map((match) => (
                                            <div className="relative" key={match.slot}>
                                                <Match
                                                    match={match}
                                                    round={round}
                                                    wildcardIds={wildcardIds}
                                                    weekLabels={cup?.week_labels}
                                                />
                                                {index < bracket.length - 1 && (
                                                    <div
                                                        aria-hidden="true"
                                                        className="absolute left-full top-1/2 hidden w-10 border-t-2 border-border lg:block"
                                                    />
                                                )}
                                            </div>
                                        ))}
                                        {index < bracket.length - 1 &&
                                            round.matches.length > 1 && (
                                                <div
                                                    aria-hidden="true"
                                                    className="absolute bottom-[calc(25%_-_3.5px)] left-[calc(100%_+_2.5rem)] top-[calc(25%_-_3.5px)] hidden border-l-2 border-border lg:block"
                                                />
                                            )}
                                        {index > 0 && (
                                            <div
                                                aria-hidden="true"
                                                className="absolute right-full top-1/2 hidden w-10 border-t-2 border-border lg:block"
                                            />
                                        )}
                                    </div>
                                </div>
                                {index === bracket.length - 1 && (
                                    <div className="mt-4 rounded-xl border-[1.5px] border-mustard bg-mustard-light px-3.5 py-[18px] text-center">
                                        <div className="text-[26px]">🏆</div>
                                        <p className="mb-1.5 mt-1 text-[9px] font-sans font-extrabold uppercase tracking-[0.24em] text-[#8a6d12]">
                                            Champion
                                        </p>
                                        {champion ? (
                                            <>
                                                <p className="break-words font-display text-[17px] font-black text-forest">
                                                    {champion.owner_name || champion.team_name}
                                                </p>
                                                {champion.owner_name &&
                                                    champion.owner_name !==
                                                        champion.team_name && (
                                                        <p className="mt-0.5 break-words text-[11px] font-sans font-bold text-text-mid">
                                                            {champion.team_name}
                                                        </p>
                                                    )}
                                            </>
                                        ) : (
                                            <p className="font-display text-[17px] font-black text-forest/50">
                                                TBD
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </React.Fragment>
                    ))}
                </div>
            )}

            <div className="border-t border-[#e7e2d5] bg-bg/60 px-[18px] py-3">
                <p
                    role="note"
                    className="text-[11px] font-sans font-semibold leading-relaxed text-text-muted"
                >
                    <span className="font-extrabold text-text-mid">Bracket rules:</span>{" "}
                    Wild Card teams are always seeded below group winners. Teams from the
                    same group are kept apart in the first knockout round whenever possible.
                </p>
            </div>
        </div>
    );
};

export default CupBracketPanel;
